import { Accordion, AccordionContent, AccordionHeader, AccordionItem, AccordionTrigger } from '@ibsys/kit';
import type { FC, ReactNode } from 'react';
import { applyNbspTypography, cn } from '@/shared/lib';

/**
 * Один уровень иерархии секции в «портянке» атрибутов.
 *
 * Рендерит заголовок (`h2`/`h3`/`h4` в зависимости от `depth`) и якорь по `id`,
 * чтобы:
 * - на секцию можно было прокрутить через URL hash (`#<id>`);
 * - `useScrollSpy` мог отследить её появление в зоне активности.
 *
 * Заголовок одновременно служит переключателем: клик сворачивает и разворачивает
 * содержимое секции вместе со всеми вложенными подсекциями. Свёрнутая секция
 * сохраняет видимый заголовок с признаком раскрытия.
 *
 * `scroll-mt-*` нужен, чтобы при `scrollIntoView` заголовок не упирался в
 * верхний край скроллящегося контейнера.
 */
type Props = {
  /** DOM `id` — одновременно якорь URL hash и цель для `IntersectionObserver`. */
  id: string;
  /** Видимое название секции. */
  title: string;
  /** Глубина в дереве (0 — корневой узел). Определяет уровень заголовка и отступ слева. */
  depth: number;
  /** Секция свёрнута: содержимое и вложенные подсекции скрыты. */
  isCollapsed: boolean;
  /** Переключение свёрнутости по клику на заголовок. */
  onToggle: () => void;
  children?: ReactNode;
};

/** CSS-классы для заголовка по глубине узла; глубины > 2 используют последний класс. */
const HEADING_CLASS_BY_DEPTH = ['text-xl font-semibold', 'text-lg font-semibold', 'text-base font-medium'];

/** Левый отступ всей секции по глубине (тейлвинд `pl-N`). */
const SECTION_PADDING_BY_DEPTH = ['pl-0', 'pl-4', 'pl-8', 'pl-12'];

/** HTML-тег заголовка по глубине. */
const HEADING_TAG_BY_DEPTH = ['h2', 'h3', 'h4', 'h5'] as const;

const pickByDepth = <T,>(arr: readonly T[], depth: number): T => arr[Math.min(depth, arr.length - 1)];

export const SectionHeading: FC<Props> = ({ id, title, depth, isCollapsed, onToggle, children }) => {
  const HeadingTag = pickByDepth(HEADING_TAG_BY_DEPTH, depth);
  const headingClass = pickByDepth(HEADING_CLASS_BY_DEPTH, depth);
  const paddingClass = pickByDepth(SECTION_PADDING_BY_DEPTH, depth);

  return (
    <section
      id={id}
      aria-labelledby={`${id}__heading`}
      // scroll-mt-4 — отступ при scrollIntoView, чтобы заголовок не упирался в верх контейнера
      className={cn('scroll-mt-4', paddingClass)}
    >
      <Accordion type="multiple" value={isCollapsed ? [] : [id]} onValueChange={onToggle}>
        <AccordionItem value={id} className="border-0">
          <AccordionHeader asChild>
            <HeadingTag id={`${id}__heading`}>
              <AccordionTrigger iconSide="left" className="py-0 gap-2 items-center cursor-pointer">
                <span className={headingClass}>{applyNbspTypography(title)}</span>
              </AccordionTrigger>
            </HeadingTag>
          </AccordionHeader>
          <AccordionContent className="flex flex-col gap-3 pt-3 pb-0">{children}</AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
};
